import Constants from '../app.constants';
import { api } from './api';
import { session } from './session';

export class AuthService {
  constructor() {
    this.Api = api;
    this.Constants = Constants;
    this.Session = session;

    this.clientCredentials = {
      client_id: this.Constants.CLIENT_ID,
      client_secret: this.Constants.CLIENT_SECRET,
    };

    this.isAdmin = false;
    this.accessList = [];
  }

  login(credentials) {
    const loginData = { ...credentials, ...this.clientCredentials };

    return this.Api.post('/api/v0/login/', loginData)
      .then(resp => {
        const data = resp.data.data[0];
        const accessToken = data.access_token_payload;

        this.createAccessList(accessToken.bamx_perms);
        this.createSession(data, credentials);
      })
      .then(() => {
        return this.loadTermsConditions();
      })
      .catch(err => {
        this.logout();
        return Promise.reject(err);
      });
  }

  logout(refreshPage) {
    this.Session.destroy();

    return this.Api.post('/api/v0/logout/').then(() => {
      if (refreshPage) window.location.assign('/v2/login');
    });
  }

  /* Login helpers */
  createAccessList(bamxPerms) {
    this.accessList = [];
    this.isAdmin = false;

    Object.keys(bamxPerms).forEach(orgKey => {
      const orgList = bamxPerms[orgKey];
      if (orgKey === 'admin') {
        this.isAdmin = true;
        this.accessList.push({
          type: 'admin',
          label: 'admin',
          orgId: 0,
          termsAccepted: true,
          isOrgAdmin: true,
        });
      } else if (orgKey === 'bamx') {
        // i.e. {bamx: {product_match: true}}
        Object.keys(orgList).forEach(accessType => {
          if (!orgList[accessType]) return;

          this.accessList.push({
            type: accessType,
            label: accessType,
            orgId: 0,
            termsAccepted: true,
            isOrgAdmin: true,
          });
        });
      } else {
        Object.keys(orgList).forEach(orgId => {
          this.accessList.push({
            type: orgKey,
            label: this.getUserRoleLabel(orgKey),
            orgId: parseInt(orgId),
            // termsAccepted gets updated by loadTermsConditions()
            termsAccepted: orgKey === 'merchants',
            isOrgAdmin: orgList[orgId].is_org_admin,
            isBillingUser: orgList[orgId].is_billing_user,
          });
        });
      }
    });
  }

  createSession(data, credentials) {
    const accessToken = data.access_token_payload;

    this.Session.create({
      authVersion: this.Constants.AUTH_VERSION,
      bamxVersion: accessToken.bamx_version || 0,
      orgId: 0,
      termsAccepted: false,
      isOrgAdmin: false,
      isAdmin: this.isAdmin,
      userId: accessToken.sub,
      userName: credentials.username,
      userRole: null,
      accessList: this.accessList,
      email: accessToken.email,
      firstName: accessToken.given_name,
      lastName: accessToken.family_name,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      exp: accessToken.exp,
    });

    return accessToken.sub;
  }

  updateSession(org, allSites = false) {
    // Update the session when user switches to another org
    org = org || {};
    const publisherType = org.publisher_type || null;

    if (org.pub_id) {
      org = {
        orgId: org.pub_id,
        type: 'publishers',
      };
    } else if (org.merch_id) {
      org = {
        orgId: org.merch_id,
        type: 'merchants',
      };
    } else {
      org = {
        orgId: org.orgId || 0,
        type: org.type,
      };
    }

    if (this.Session.isAdmin) {
      Object.assign(org, {
        type: org.type || 'admin',
        isOrgAdmin: true,
        termsAccepted: true,
      });
    } else {
      org = this.getOrgDataFromAccessList(org);
    }

    /*
     * New User flow handles terms and conditions redirect for primary and secondary users
     * Update session even if user is not an org admin or has not accepted terms.
     */
    try {
      this.Session.update({
        orgId: org.orgId,
        userRole: org.type,
        isOrgAdmin: org.isOrgAdmin,
        termsAccepted: org.termsAccepted,
        publisher_type: publisherType,
        allSites,
      });
      return this.Session;
    } catch {
      return Promise.reject(new Error('Could not set user to an account'));
    }
  }

  loadOrganizations() {
    const promise = this.Session.isAdmin
      ? Promise.resolve({})
      : this.buildOrgRequests(this.Session);

    return Promise.all(promise).then(responses => {
      let organizations = [];
      responses.forEach(resp => {
        organizations = organizations.concat(resp.data.data);
      });

      return this.buildOrgDicts(organizations);
    });
  }

  loadTermsConditions() {
    let org = null;
    const loadData = this.Session.isAdmin ? Promise.resolve({}) : this.loadOrganizations();

    return loadData.then(orgsByType => {
      this.Session.accessList.forEach(accessObject => {
        let orgData;
        try {
          const orgs = orgsByType[accessObject.type];
          orgData = orgs[accessObject.orgId];
          if (orgData.hasOwnProperty('terms_accepted')) {
            accessObject.termsAccepted = orgData.terms_accepted;
          }
        } catch (e) {
          console.warn('error getting org type');
        }
        /*
         * New User flow handles terms and conditions redirect for primary and secondary users
         * Update session even if user is not an org admin or has not accepted terms.
         */
        if (accessObject.orgId === this.Session.orgId || !org) {
          org = accessObject;
          org.publisher_type = orgData ? orgData.publisher_type : null;
        }
      });

      return this.updateSession(org);
    });
  }

  /* Password functions */
  changePassword(credentials, reloadPage) {
    Object.assign(credentials, this.clientCredentials);

    return this.Api.post('/api/v0/accounts/password/change/', credentials).then(() => {
      if (reloadPage) window.location.reload();
    });
  }

  completeResetPassword(resetToken, newPassword) {
    const data = {
      reset_token: resetToken,
      new_password: newPassword,
      ...this.clientCredentials,
    };
    return this.Api.post('/api/v0/accounts/password/reset_completions/', data);
  }

  requestResetPassword(username) {
    const data = { username, ...this.clientCredentials };
    return this.Api.post('/api/v0/accounts/password/reset_requests/', data);
  }

  /* Helper functions */
  buildOrgRequests(user) {
    /*
     * Pass user object as an argument so this function
     * can be used by the OrgSelect component
     */
    let requests = [];
    let pubParams = {};
    let merchParams = {};

    if (user.isAdmin) {
      // Not passing org IDs returns all entities
      requests = [this.Api.get('/api/v0/merchants/'), this.Api.get('/api/v0/publishers/')];
    } else {
      const [merchantIds, publisherIds] = this.getOrgIds(user);

      if (merchantIds.length) {
        merchParams = { merchants: merchantIds.join(',') };
        requests.push(this.Api.get('/api/v0/merchants/', merchParams));
      }
      if (publisherIds.length) {
        pubParams = { publishers: publisherIds.join(',') };
        requests.push(this.Api.get('/api/v0/publishers/', pubParams));
      }
    }

    return requests;
  }

  buildOrgDicts(organizations) {
    const orgDicts = { merchants: {}, publishers: {} };

    organizations.forEach(org => {
      const orgType = org.pub_id ? 'publishers' : 'merchants';
      const orgId = org.pub_id || org.merch_id || 0;
      orgDicts[orgType][orgId] = org;
    });

    return orgDicts;
  }

  getOrgIds(user) {
    // Get the IDs of organizations the user has access to from accessList
    const merchantIds = [];
    const publisherIds = [];

    /*
     * accessList: [{
     *  type: "publishers", label: "publisher", orgId: 1054,
     *  termsAccepted: true, isOrgAdmin: false,
     * },]
     */
    user.accessList.forEach(org => {
      if (org.type === 'merchants') {
        merchantIds.push(org.orgId);
      } else if (org.type === 'publishers') {
        publisherIds.push(org.orgId);
      }
    });

    return [merchantIds, publisherIds];
  }

  getOrgDataFromAccessList(org) {
    return (
      this.Session.accessList.find(accessObject => {
        return org.orgId === accessObject.orgId && org.type === accessObject.type;
      }) || {}
    );
  }

  getUserRoleLabel(role) {
    switch (role) {
      case 'merchants':
        return 'retailer';
      case 'publishers':
        return 'publisher';
      default:
        return role;
    }
  }
}

export const authService = new AuthService();
